import { type JSX, type PropsWithChildren, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import Loading from '@common/components/Loading';
import useAccessRights from '@common/hooks/useAccessRights';
import useEulaRequirement from '@common/hooks/useEulaRequirement';
import { useAppSelector } from '@common/store';
import { UserState } from '@common/types/store';
import { type RightName } from '@common/utils/accessRights';

// Either the props have both requiredRight and onViolation or neither of them
interface PrivateRoutePropsWithLevelCheck {
  requiredRight: RightName;
  onViolation: string | JSX.Element;
}

interface PrivateRoutePropsNoLevelCheck {
  requiredRight?: never;
  onViolation?: never;
}

type PrivateRoutePropsWithChildren =
  & PropsWithChildren<
    | PrivateRoutePropsWithLevelCheck
    | PrivateRoutePropsNoLevelCheck
  >
  & { isTermsRoute?: boolean; };

function PrivateRouteContainer({
  requiredRight,
  onViolation,
  children,
  isTermsRoute,
}: PrivateRoutePropsWithChildren): JSX.Element {
  const userState = useAppSelector((state) => state.main.userState);
  const rights = useAccessRights();
  const { currentUser } = rights;
  const mustAcceptEula = useEulaRequirement();
  const [preserveLocation, setPreserveLocation] = useState(true);
  const location = useLocation();

  useEffect(
    () => {
      if (userState === UserState.LoggedIn) {
        setPreserveLocation(false);
      }
    },
    [userState],
  );

  // Not logged in yet - show loading
  if (userState !== UserState.LoggedIn && userState !== UserState.LoggedOut) {
    return <Loading />;
  }

  // Logged out - redirect to login
  if (userState === UserState.LoggedOut) {
    return (
      <Navigate
        to="/login"
        state={preserveLocation ? { from: location } : undefined}
      />
    );
  }

  // Check if user level requirements are met
  const userLevelCheckPending = requiredRight && !currentUser;
  if (userLevelCheckPending) {
    return <Loading />;
  }

  const hasRequiredLevel = !requiredRight || rights[requiredRight];

  // User doesn't have required level - show violation content
  if (!hasRequiredLevel) {
    if (typeof onViolation === 'string' && onViolation.startsWith('/')) {
      return <Navigate to={onViolation} />;
    }
    return (
      <>
        {onViolation}
      </>
    );
  }

  // User must accept EULA first
  if (currentUser && mustAcceptEula && !isTermsRoute) {
    return <Navigate to="/terms" replace relative="route" />;
  }

  // All checks passed - render children or outlet
  return children
    ? (
      <>
        {children}
      </>
    )
    : <Outlet />;
}

export default PrivateRouteContainer;
